import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import type { UseFormTrigger } from 'react-hook-form';
import { onboardPatientRogerIntakeIntakeIdOnboardPatientRogerPost } from '../../services/api/sdk.gen';
import type { RogerOnboardPatientResponse } from '../../services/api/types.gen';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';
import { parseRogerOnboardingError } from './rogerOnboardingErrors';
import type { SurfaceableResultFields } from '../../utils/apiErrorDetail';

/** Roger onboard response plus optional surfaceable-business-error fields. */
export type RogerOnboardResultWithSurfaceable = RogerOnboardPatientResponse &
  SurfaceableResultFields;

export interface RogerCreateResult {
  response: RogerOnboardResultWithSurfaceable;
}

interface UseRogerCreationOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
  trigger: UseFormTrigger<CreateEpisodeStepFormData>;
}

interface UseRogerCreationReturn {
  creatingInRoger: boolean;
  rogerCreateResult: RogerCreateResult | null;
  handleCreateInRoger: () => Promise<void>;
}

/**
 * Hook for creating patients in Roger.
 * Manages the creation state and handles the API call.
 */
export function useRogerCreation({
  intakeId,
  intakeStatus,
  trigger,
}: UseRogerCreationOptions): UseRogerCreationReturn {
  const [creatingInRoger, setCreatingInRoger] = useState(false);
  const [rogerCreateResult, setRogerCreateResult] =
    useState<RogerCreateResult | null>(null);

  const clearRogerCreateResult = useCallback(() => {
    setRogerCreateResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearRogerCreateResult
  );

  const handleCreateInRoger = useCallback(async () => {
    if (!intakeId) return;

    // Validate form before calling API
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    setCreatingInRoger(true);
    setRogerCreateResult(null);

    try {
      const response =
        await onboardPatientRogerIntakeIntakeIdOnboardPatientRogerPost({
          path: { intake_id: intakeId },
          throwOnError: true,
        });

      if (response.data) {
        setRogerCreateResult({ response: response.data });
      }
    } catch (error) {
      console.error('Error creating patient in Roger:', error);

      const { message, surfaceable, errorCode } =
        parseRogerOnboardingError(error);
      setRogerCreateResult({
        response: {
          success: false,
          message,
          ...(surfaceable ? { surfaceable: true, error_code: errorCode } : {}),
        },
      });
    } finally {
      setCreatingInRoger(false);
    }
  }, [intakeId, trigger]);

  return {
    creatingInRoger,
    rogerCreateResult,
    handleCreateInRoger,
  };
}
